/**
 * Utility functions round about Arrays.
 *
 * @packageDocumentation
 * @module misc/arrays
 * @preferred
 */
/**
 * Array.filter accepts only synchronous callbacks.
 * This helper provides a simple way to filter an array using an async callback.
 *
 * ```
 * const filteredArray = await asyncFilter(array, async (e) => {
 *     const foo = await doStuff(e);
 *     return foo === something;
 * });
 * ```
 *
 * @param arr The array to filter
 * @param callback An async callback function to test each element of the array. Return `true` to keep the element, `false` otherwise.
 *
 * @returns The filtered array
 */
export const asyncFilter = async (arr, callback) => {
    return (await Promise.all(arr.map(async (item) => ((await callback(item)) ? item : false)))).filter((i) => i !== false);
};
/**
 * Array.map accepts only synchronous callbacks.
 * This helper provides a simple way to use map on an array using an async callback.
 *
 * ```
 * const ids = await asyncMap(array, async (e) => {
 *     return {
 *          id: await getId(e);
 *     };
 * });
 * ```
 *
 * @param arr The array to apply a mapping to
 * @param callback An async callback function to apply on each element of the array
 */
export const asyncMap = async (arr, callback) => {
    return await Promise.all(arr.map(async (item, index) => await callback(item, index)));
};
/**
 * Helper function to apply multiple filters to an array.
 * Useful if you need to compose the filters at runtime depending on some parameters.
 *
 * ```
 * const elements = [...];
 * // compose your filters somehow
 * const filters = [...]
 * const filtered = applyFilters(elements, filters);
 * ```
 *
 * If an empty filters array is passed, the input array is returned as is.
 */
export function applyFilters(entries, filters) {
    if (filters.length === 0) {
        return entries;
    }
    return entries.filter((entry, index, array) => filters.every((filter) => filter(entry, index, array)));
}
